/**
 * AsterWallet web — App shell.
 *
 * Routes:
 *   /signin              → SignIn (phone number step, public)
 *   /signin/otp          → SignInOtp (6-box OTP step, public)
 *   /                    → Home (protected)
 *   /activity            → Activity (protected)
 *   /statements          → Statements (protected · Reg E row 3)
 *   /markets             → Markets stub (Stage II — Coming soon)
 *   /settings            → Settings hub (all 9 legally-required rows)
 *   /settings/:slug      → Honest-placeholder for rows whose endpoint
 *                          isn't live yet
 *   /legal/:slug         → Statutory-document viewer
 *   /gallery             → §9 component gallery (dev only)
 *   *                    → 404
 *
 * ProtectedRoute checks the AuthContext and redirects to /signin when
 * the SPA doesn't have an active web-session cookie.
 *
 * Sign-in is a two-step phone/OTP flow (migration 013), replacing the
 * dev-only `DEV-SIG:` bearer bypass on production. See
 * pages/SignIn.tsx and pages/SignInOtp.tsx for the state machine.
 */

import { ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, Link } from "react-router-dom";
import { Activity } from "./pages/Activity";
import { Gallery } from "./pages/Gallery";
import { Home } from "./pages/Home";
import { Legal } from "./pages/Legal";
import { Markets } from "./pages/Markets";
import { Placeholder } from "./pages/Placeholder";
import { Settings } from "./pages/Settings";
import { SignIn } from "./pages/SignIn";
import { SignInOtp } from "./pages/SignInOtp";
import { SignupLegal } from "./pages/signup/steps/Legal";
import { SignupName } from "./pages/signup/steps/Name";
import { SignupDob } from "./pages/signup/steps/Dob";
import { SignupAddress } from "./pages/signup/steps/Address";
import { SignupSsn } from "./pages/signup/steps/Ssn";
import { SignupVerifying } from "./pages/signup/steps/Verifying";
import {
  SignupBlocked,
  SignupDocuments,
  SignupPending,
  SignupRejected,
} from "./pages/signup/steps/Terminal";
import { SiteGate } from "./pages/SiteGate";
import { Statements } from "./pages/Statements";
import { ThemeToggle, ToastProvider } from "./components";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import styles from "./App.module.css";

// ---------------------------------------------------------------------------
// Protected wrapper.
// ---------------------------------------------------------------------------

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === "loading") {
    return (
      <main className={styles.loadingShell}>
        <p className={styles.loadingText}>Resolving session…</p>
      </main>
    );
  }
  if (status === "signed_out") {
    return <Navigate to="/signin" replace />;
  }
  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// 404.
// ---------------------------------------------------------------------------

function NotFound() {
  return (
    <main className={styles.notFound}>
      <ThemeToggle />
      <div className={styles.notFoundInner}>
        <p className={styles.notFoundCode}>404</p>
        <p className={styles.notFoundText}>Nothing at that URL.</p>
        <Link to="/" className={styles.notFoundBack}>
          Back home
        </Link>
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// App.
//
// PROD vs DEV split:
//
//   * `import.meta.env.DEV` is TRUE only during `vite dev` and FALSE for
//     every production `vite build`. Vite's terser dead-code-eliminates
//     the false branch so the compiled bundle contains only the branch
//     selected at build time.
//
//   * Dev builds keep the site passphrase curtain (SiteGate) so local
//     hand-testing on preview URLs isn't wide open.
//
//   * Prod builds strip the curtain — the site has real auth now
//     (phone/OTP → session_kind='web' via migration 013), served over
//     HTTPS on the public domain.
//
// See routes/dev.py on the backend side: that module refuses to import
// when ENVIRONMENT=production, so /api/_dev/impersonate 404s in prod
// even if a build somehow retains a reference to it.
// ---------------------------------------------------------------------------

const IS_DEV_BUILD = import.meta.env.DEV;

function AppRoutes() {
  return (
    <BrowserRouter
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signin/otp" element={<SignInOtp />} />
        {/* Signup entry — same phone-OTP mechanics as sign-in but
            posts to /v1/auth/web/signup on success and routes into the
            CIP flow. See pages/SignIn.tsx and SignInOtp.tsx for the
            mode-branching detail. */}
        <Route path="/signup" element={<SignIn />} />
        <Route path="/signup/otp" element={<SignInOtp />} />
        {/*
          Signup — five data-entry screens + five terminal/transitional
          screens. They REQUIRE a session (the user has already exchanged
          a verification_token for a session via /v1/auth/web/signup) so
          they sit inside ProtectedRoute. The terminal screens (rejected,
          blocked, pending, documents) also require a session so we can
          check identity_status; that's fine because the session survives
          verification even in the rejected/blocked cases (only the CIP
          state is terminal, not the account).
        */}
        <Route
          path="/signup/legal"
          element={
            <ProtectedRoute>
              <SignupLegal />
            </ProtectedRoute>
          }
        />
        <Route
          path="/signup/name"
          element={
            <ProtectedRoute>
              <SignupName />
            </ProtectedRoute>
          }
        />
        <Route
          path="/signup/dob"
          element={
            <ProtectedRoute>
              <SignupDob />
            </ProtectedRoute>
          }
        />
        <Route
          path="/signup/address"
          element={
            <ProtectedRoute>
              <SignupAddress />
            </ProtectedRoute>
          }
        />
        <Route
          path="/signup/ssn"
          element={
            <ProtectedRoute>
              <SignupSsn />
            </ProtectedRoute>
          }
        />
        <Route
          path="/signup/verifying"
          element={
            <ProtectedRoute>
              <SignupVerifying />
            </ProtectedRoute>
          }
        />
        <Route
          path="/signup/pending"
          element={
            <ProtectedRoute>
              <SignupPending />
            </ProtectedRoute>
          }
        />
        <Route
          path="/signup/documents"
          element={
            <ProtectedRoute>
              <SignupDocuments />
            </ProtectedRoute>
          }
        />
        <Route
          path="/signup/rejected"
          element={
            <ProtectedRoute>
              <SignupRejected />
            </ProtectedRoute>
          }
        />
        <Route
          path="/signup/blocked"
          element={
            <ProtectedRoute>
              <SignupBlocked />
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />
        <Route
          path="/activity"
          element={
            <ProtectedRoute>
              <Activity />
            </ProtectedRoute>
          }
        />
        <Route
          path="/statements"
          element={
            <ProtectedRoute>
              <Statements />
            </ProtectedRoute>
          }
        />
        <Route
          path="/markets"
          element={
            <ProtectedRoute>
              <Markets />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/:slug"
          element={
            <ProtectedRoute>
              <Placeholder />
            </ProtectedRoute>
          }
        />
        <Route
          path="/legal/:slug"
          element={
            <ProtectedRoute>
              <Legal />
            </ProtectedRoute>
          }
        />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export function App() {
  const inner = (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
  return (
    // ErrorBoundary MUST be the outermost tree element (except for
    // ToastProvider, which has no render logic that can fail with
    // hooks-shape errors — it's a plain context wrapper). Any React
    // runtime error thrown ANYWHERE below this line is caught and
    // replaced with a readable recovery card instead of an empty
    // #root that shows through as a black canvas. See
    // /components/ErrorBoundary.tsx header for the incident that
    // motivated adding this.
    <ErrorBoundary>
      <ToastProvider>
        {IS_DEV_BUILD ? <SiteGate>{inner}</SiteGate> : inner}
      </ToastProvider>
    </ErrorBoundary>
  );
}
